<template>
  <base-content>
    <template v-slot:top>
      <project-heading back>Financial Details</project-heading>
    </template>
    <template v-if="loaded">
      <form @submit.prevent="submit">
        <div class="row">
          <div class="col-sm-8">
            <base-card>
              <template v-slot:header>
                <base-card-heading>Project Estimates</base-card-heading>
              </template>
              <div class="row">
                <div class="col-sm-6">
                  <base-field addon-left-icon="pound-sign" label="GDV">
                    <base-input v-model="payload.financials.gdv" numeric placeholder="gdv" required />
                  </base-field>
                </div>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Site Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalSiteCosts) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.siteCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.site[key]" numeric :placeholder="name.toLowerCase()" />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Build Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalBuildCosts) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.buildCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.build[key]" numeric :placeholder="name.toLowerCase()" />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Professional Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalProfessionalCosts) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.professionalCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.professional[key]" numeric :placeholder="name.toLowerCase()" />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Sales Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalSalesCost) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.salesCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.sales[key]" numeric :placeholder="name.toLowerCase()" />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Finance Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalFinanceCosts) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.financeCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.finance[key]" numeric :placeholder="name.toLowerCase()" />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Other Costs</base-card-heading>
                <div>
                  {{ $formatting.toCurrency(payload.financials.totalOtherCosts) }}
                </div>
              </template>
              <div class="row">
                <template v-for="(name, key) in fields.otherCosts">
                  <div class="col-sm-6" :key="key">
                    <base-field addon-left-icon="pound-sign" :label="name">
                      <base-input v-model="payload.financials.other[key]" numeric :placeholder="name.toLowerCase()" required />
                    </base-field>
                  </div>
                </template>
              </div>
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Additional Information</base-card-heading>
              </template>
              <p>
                Please provide a breakdown of professional fees and other costs here.
              </p>
              <wysiwyg-editor v-model="payload.financials.additionalInformation" />
            </base-card>
            <base-card>
              <template v-slot:header>
                <base-card-heading>Confirmation</base-card-heading>
              </template>
              <p>
                I confirm that the information given is true, accurate and not misleading and acknolwedge
                that the information will be used to form our assessment of any requests for loans and
                we may therefore rely on any statement provided.
              </p>
              <base-button icon-right="arrow-right" :loading="saving" primary submit>
                Save Financials
              </base-button>
            </base-card>
          </div>
          <div class="col-sm-4">
            <base-card>
              <template v-slot:header>
                <base-card-heading>Key Financials</base-card-heading>
              </template>
              <project-calculated-financials v-model="payload"/>
            </base-card>
          </div>
        </div>
      </form>
    </template>
    <template v-else>
      <base-loader />
    </template>
  </base-content>
</template>

<script>
import { mapState } from 'vuex'
import { projectFinancials } from '@/data'
import { ProjectCalculations } from '@/libs'

import ProjectCalculatedFinancials from '@/components/ProjectCalculatedFinancials.vue'
import ProjectHeading from '@/components/ProjectHeading.vue'
import WysiwygEditor from '@/components/WysiwygEditor.vue'

export default {
  async created () {
    await this.$store.dispatch('core/projects/get', this.$route.params.id)

    this.payload = this.$_.cloneDeep(this.project)
    this.payload.stages.financials = true

    // Skip funding confirmation if an admin.
    if (this.$auth.admin()) {
      this.payload.stages.funding = true
    }

    this.calculate()

    this.fields = projectFinancials
    this.loaded = true
  },

  data: () => ({
    fields: {},
    loaded: false,
    payload: {},
    saving: false
  }),

  components: {
    ProjectCalculatedFinancials,
    ProjectHeading,
    WysiwygEditor
  },

  computed: {
    ...mapState({
      project: store => store.core.projects.entity
    })
  },

  methods: {
    calculate () {
      const calculate = new ProjectCalculations(this.payload)

      this.payload.financials.totalCost = calculate.getTotalCost()
      this.payload.financials.totalBuildCosts = calculate.getTotalBuildCosts()
      this.payload.financials.totalFinanceCosts = calculate.getTotalFinanceCosts()
      this.payload.financials.totalOtherCosts = calculate.getTotalOtherCosts()
      this.payload.financials.totalProfessionalCosts = calculate.getTotalProfessionalCosts()
      this.payload.financials.totalSalesCosts = calculate.getTotalSalesCosts()
      this.payload.financials.totalSiteCosts = calculate.getTotalSiteCosts()
    },

    async submit () {
      this.saving = true

      await this.$store.dispatch('core/projects/save', this.payload)

      this.$flash.success('Project financials successfully saved.')

      this.$router.push({ name: 'projects.dashboard', params: { id: this.project._id } })
    }
  },

  watch: {
    payload: {
      deep: true,
      handler () {
        this.calculate()
      }
    }
  }
}
</script>
